// Pricing
import "@vp/pricing-context-module";
// Intersection Observer polyfill
import "intersection-observer";

import * as reader from "@vp/ab-reader";

import {
  AnalyticsContextProvider,
  ExperimentImpression,
} from "../contexts/AnalyticsContextProvider";
import { BOOKEND_TYPES, BookendsMetaTag, Footer, Header } from "@vp/esi-bookends";
import {
  BoundedContent,
  Box,
  MainContentStart,
  ScreenClassProvider,
  SiteContent,
  SiteMain,
} from "@vp/swan";
// Types
import {
  CTACloudinaryImage,
  ContentfulCloudinaryImageComponent,
  ContentfulDictionaryEntry,
  ContentfulPageProductPageV2,
  ContentfulSimpleLink,
  Seo,
} from "../types/ContentfulTypes";
import { CouponCalloutProvider, Locale, PageId } from "@vp/coupon-callout";
import React, { FunctionComponent, useEffect } from "react";

import Analytics from "../components/Analytics";
import AvailabilityAlertBox from "../components/AvailabilityAlertBox";
import BffDataContextProvider from "../contexts/BffDataContextProvider";
// @ts-ignore
import { Breadcrumbs } from "@vp/breadcrumbs-react-component";
import { CampaignProvider } from "@vp/campaign-callouts";
import { CartTenantDetails } from "@vp/cart-client";
import { DebugContextProvider } from "../components/debug/DebugContextProvider";
import { DebugInfo } from "../components/debug/DebugInfo";
// Components
import DesignServices from "../components/DesignServices";
import { DesignServicesContextProvider } from "../contexts/DesignServicesContextProvider";
import ErrorBoundary from "../components/ErrorBoundary";
import ExperimentsProvider from "../contexts/ExperimentsProvider";
import { FeedTemplateProvider } from "../contexts/FeedTemplateProvider";
import FrequentlyBoughtTogether from "../components/recommendations/FrequentlyBoughtTogether";
import { GalleryTemplateProvider } from "../contexts/GalleryTemplateProvider";
import Helmet from "react-helmet";
import { Icons } from "../utils/Icons";
import { IdentityProvider } from "../contexts/IdentityProvider";
import MailingServiceTermsOfUse from "../components/mailingServices/MailingServiceTermsOfUse";
import { MetadataWrapper } from "../components/MetadataWrapper";
import PageConfig from "../utils/PageConfig";
import { PageContext } from "../types/Global";
import { PageStaticDataContextProvider } from "../contexts/PageStaticDataContextProvider";
import { Placeholder } from "../pluginsInfrastructure/PluginRegistryTypes";
import PluginPlaceholder from "../pluginsInfrastructure/PluginPlaceholder";
import { PluginRegistryProvider } from "../pluginsInfrastructure/PluginRegistryContextProvider";
import { PluginStateProvider } from "../pluginsInfrastructure/PluginStateProvider";
import { PricingContextProvider } from "../contexts/PricingContext";
import ProductBrowsingHistory from "../components/recommendations/ProductBrowsingHistory";
import { ProductBuilderContextProvider } from "../contexts/ProductBuilderContextProvider";
import ProductContextProvider from "../contexts/ProductContextProvider";
import ProductPageBFFPreloader from "../components/ProductPageBFFPreloader";
import { ProductPageTabContextProvider } from "../contexts/ProductPageTabContext";
import ProductPageTabs from "../components/ProductPageTabs";
import PromoBarContainer from "../components/PromobarContainer";
import { ReferentialImageContextProvider } from "../contexts/ReferentialImagesContext";
import RelatedProducts from "../components/recommendations/RelatedProducts";
import Reviews from "../components/Reviews/index";
import { SeoMetadata } from "@vp/pdp-metadata";
import { SitewidePromoBar } from "@vp/sitewide-promo-bar";
import SnackbarContextProvider from "../contexts/SnackbarContext";
import StructuredData from "../components/StructuredData";
import { StructuredDataContextProvider } from "../contexts/StructuredDataContextProvider";
import { SwanConfiguration } from "../components/SwanConfiguration";
import TemplatesGalleryComponent from "../components/gallery/TemplatesGalleryComponent";
import Validation from "../utils/validation";
// Utils
import { addDictionaryEntryTranslations } from "../utils/Translation";
import { initCA } from "@vp/chat-anywhere";

// Other Vistaprint components

export interface IndexProps {
  data: {
    pageSeo: Seo;
    authLibrary: any;
    promobarConfig: React.ComponentProps<typeof SitewidePromoBar>["promoBarConfig"];
    vistacartTenant: CartTenantDetails;

    appleLogoIcon: ContentfulCloudinaryImageComponent;
    designServicesHeroImage: ContentfulCloudinaryImageComponent;
    printedProductDesignImage: ContentfulCloudinaryImageComponent;
    satisfactionGuaranteedImage: ContentfulCloudinaryImageComponent;
    windowsLogoIcon: ContentfulCloudinaryImageComponent;
    mailingServicesIcon: ContentfulCloudinaryImageComponent;
    workWithDesignerIcon: ContentfulCloudinaryImageComponent<CTACloudinaryImage>;

    contentfulPageProductPageV2: ContentfulPageProductPageV2;
    enableCampaignCallouts: boolean;
    hideReviews: boolean;

    offlineEditorMacDownload: ContentfulSimpleLink;
    offlineEditorWindowsDownload: ContentfulSimpleLink;
    onlinePhotobookEditor: ContentfulSimpleLink;

    accessoryTabHeader: ContentfulDictionaryEntry;
    accessoryTabCallout: ContentfulDictionaryEntry;
    availabilityAlertBox: ContentfulDictionaryEntry;
    bleed: ContentfulDictionaryEntry;
    clearSelection: ContentfulDictionaryEntry;
    componentError: ContentfulDictionaryEntry;
    defaultCtaText: ContentfulDictionaryEntry;
    designServices: ContentfulDictionaryEntry;
    designServicesCTA: ContentfulDictionaryEntry;
    designServicesDescription: ContentfulDictionaryEntry;
    designServicesEyebrow: ContentfulDictionaryEntry;
    designServicesSubheader: ContentfulDictionaryEntry;
    designServicesTitle: ContentfulDictionaryEntry;
    designSpecsInfo: ContentfulDictionaryEntry;
    dimensionsTabHeader: ContentfulDictionaryEntry;
    dismissAlert: ContentfulDictionaryEntry;
    downloadTemplate: ContentfulDictionaryEntry;
    downloadTemplateInfo: ContentfulDictionaryEntry;
    designInstructions: ContentfulDictionaryEntry;
    designInstructionsText: ContentfulDictionaryEntry;
    dropdownIncompatibleMessage: ContentfulDictionaryEntry;
    editorOptionsTabHeader: ContentfulDictionaryEntry;
    faqHeader: ContentfulDictionaryEntry;
    faqTabHeader: ContentfulDictionaryEntry;
    finishingOptionsTabHeader: ContentfulDictionaryEntry;
    finishingOptionsTabContentTitle: ContentfulDictionaryEntry;
    fullRangedHelperText: ContentfulDictionaryEntry;
    heroDefaultAltText: ContentfulDictionaryEntry;
    heroThumbnailAltText: ContentfulDictionaryEntry;
    heroReferentialImageAltText: ContentfulDictionaryEntry;
    heroReferentialImageThumbnailAltText: ContentfulDictionaryEntry;
    inputErrorAboveMaximum: ContentfulDictionaryEntry;
    inputErrorBelowMinimum: ContentfulDictionaryEntry;
    inputErrorBetweenRanges: ContentfulDictionaryEntry;
    inputErrorGeneral: ContentfulDictionaryEntry;
    inputErrorNotANumber: ContentfulDictionaryEntry;
    inputErrorPrecision: ContentfulDictionaryEntry;
    inputErrorRequiredField: ContentfulDictionaryEntry;
    requiredInformationErrorToast: ContentfulDictionaryEntry;
    heroImageAltTextPrefix: ContentfulDictionaryEntry;
    mailingListTemplateTabHeader: ContentfulDictionaryEntry;
    mailingServiceTabHeader: ContentfulDictionaryEntry;
    mailingServiceTermsOfUseCallout: ContentfulDictionaryEntry;
    mailingServiceTermsOfUseContent: ContentfulDictionaryEntry;
    mailingServicesDescription: ContentfulDictionaryEntry;
    outOfStockBanner: ContentfulDictionaryEntry;
    outOfStockButtonAttribute: ContentfulDictionaryEntry;
    overviewTabHeader: ContentfulDictionaryEntry;
    partialRangedHelperText: ContentfulDictionaryEntry;
    priceChartTabHeader: ContentfulDictionaryEntry;
    printedProductDesign: ContentfulDictionaryEntry;
    productSpecsTemplates: ContentfulDictionaryEntry;
    qty: ContentfulDictionaryEntry;
    safe: ContentfulDictionaryEntry;
    safeArea: ContentfulDictionaryEntry;
    seeDesignServices: ContentfulDictionaryEntry;
    seeDetails: ContentfulDictionaryEntry;
    specsAndTemplatesLink: ContentfulDictionaryEntry;
    shopWithYourDesignWarningMessage: ContentfulDictionaryEntry;
    sizeChartsTabHeader: ContentfulDictionaryEntry;
    specsAndTemplatesTabHeader: ContentfulDictionaryEntry;
    trim: ContentfulDictionaryEntry;
    vortexToggleView: ContentfulDictionaryEntry;
    vortexLoadError: ContentfulDictionaryEntry;
    vortexLoadingPreview: ContentfulDictionaryEntry;
    vortexActionsFront: ContentfulDictionaryEntry;
    vortexActionsBack: ContentfulDictionaryEntry;
    vortexActionsFolded: ContentfulDictionaryEntry;
    vortexActionsUnfolded: ContentfulDictionaryEntry;
    vortexActionsOpen: ContentfulDictionaryEntry;
    vortexActionsClose: ContentfulDictionaryEntry;
  };
  pageContext: PageContext;
}

const Index: FunctionComponent<IndexProps> = (props: IndexProps) => {
  const { pageContext } = props;
  const { item: data, seo } = pageContext;
  const { locale, country, productBuilderOptions, defaultVatIncl } = pageContext;

  reader.initialize();

  const mpv = data.mpv;

  // Run-time validation of data for alerting
  useEffect(() => {
    Validation.validate(data);
  }, []);

  // Init Chat
  useEffect(() => {
    initCA("", "100px");
  }, []);

  // Initialize translation dictionary
  addDictionaryEntryTranslations(data.translations);

  const icons = Icons.getInstance();
  icons.addToIconData("appleLogoIcon", data.appleLogoIcon);
  icons.addToIconData("designServicesHeroImage", data.designServicesHeroImage);
  icons.addToIconData("printedProductDesignImage", data.printedProductDesignImage);
  icons.addToIconData("satisfactionGuaranteedImage", data.satisfactionGuaranteedImage);
  icons.addToIconData("windowsLogoIcon", data.windowsLogoIcon);
  icons.addToIconData("mailingServicesIcon", data.mailingServicesIcon);
  icons.addToIconData("workWithDesignerIcon", data.workWithDesignerIcon);

  const breadcrumbs = pageContext.breadcrumbs;
  const experimentImpression: ExperimentImpression | undefined =
    pageContext.experimentKey && pageContext.variationKey
      ? {
          experimentKey: pageContext.experimentKey,
          variationKey: pageContext.variationKey,
        }
      : undefined;

  return (
    <>
      <SnackbarContextProvider>
        <PageStaticDataContextProvider
          pageContentfulProductPageV2={data}
          pageContext={pageContext}
          vistacartTenant={pageContext.vistacartTenant}
        >
          <IdentityProvider locale={locale}>
            <PricingContextProvider defaultVatIncl={pageContext.defaultVatIncl}>
              <ProductContextProvider productBuilderOptions={pageContext.productBuilderOptions}>
                <ExperimentsProvider>
                  <CampaignProvider locale={locale} tenant={PageConfig.tenant}>
                    <GalleryTemplateProvider locale={locale} mpv={mpv}>
                      <FeedTemplateProvider locale={locale} mpv={mpv}>
                        <BffDataContextProvider>
                          <AnalyticsContextProvider
                            experimentImpression={experimentImpression}
                            decision={pageContext.decision}
                          >
                            <ReferentialImageContextProvider>
                              <ProductBuilderContextProvider>
                                <ProductPageTabContextProvider>
                                  <PluginRegistryProvider
                                    allPluginsData={pageContext.allPluginsData}
                                  >
                                    <PluginStateProvider>
                                      <ProductPageBFFPreloader
                                        productBuilderOptions={pageContext.productBuilderOptions}
                                        seo={seo}
                                        pageUrl={data.pageUrl}
                                        reviews={pageContext.reviews}
                                        heroGallery={data.heroGallery}
                                        defaultVatIncl={defaultVatIncl}
                                        tabs={data.globalTabs}
                                      />

                                      <StructuredDataContextProvider
                                        locale={locale}
                                        country={country}
                                        mpv={mpv}
                                        productBuilderOptions={productBuilderOptions}
                                        defaultVatIncl={defaultVatIncl}
                                      >
                                        <StructuredData
                                          mpv={mpv}
                                          canonicalDomain={pageContext.canonicalDomain}
                                          seo={seo}
                                          pageUrl={data.pageUrl}
                                          reviews={pageContext.reviews}
                                          heroGallery={data.heroGallery}
                                          defaultVatIncl={defaultVatIncl}
                                          tabs={data.globalTabs}
                                        />
                                      </StructuredDataContextProvider>
                                      <ScreenClassProvider>
                                        <CouponCalloutProvider
                                          baseConfig={{
                                            locale: pageContext.locale as Locale,
                                            tenant: PageConfig.tenant,
                                            pageId: PageId.PDP,
                                          }}
                                        >
                                          <SiteContent>
                                            <DebugInfo pageContext={pageContext} />
                                            <div id="header-padding"></div>
                                            <Header
                                              type={BOOKEND_TYPES.FULL}
                                              tenant={PageConfig.tenant}
                                              locale={locale}
                                              showStickyHeader={true}
                                              trackingConfiguration={
                                                pageContext.trackingConfiguration
                                              }
                                            />
                                            <SiteMain>
                                              <MainContentStart />
                                              <PluginPlaceholder
                                                placeholderType={Placeholder.Top}
                                              />
                                              <DesignServicesContextProvider
                                                designServices={data.designServices}
                                              >
                                                <PromoBarContainer
                                                  locale={pageContext.locale}
                                                  promoBarConfig={pageContext.promobarConfig}
                                                  trackingConfiguration={
                                                    pageContext.trackingConfiguration
                                                  }
                                                />
                                                <ErrorBoundary>
                                                  <Box marginBottom="0">
                                                    <BoundedContent paddingY={"2"}>
                                                      <AvailabilityAlertBox
                                                        breadcrumbs={breadcrumbs}
                                                      />
                                                      <Breadcrumbs data={breadcrumbs} />
                                                    </BoundedContent>
                                                  </Box>
                                                  <PluginPlaceholder
                                                    placeholderType={Placeholder.Hero}
                                                  />
                                                </ErrorBoundary>
                                                <ErrorBoundary>
                                                  <Box marginY="between-sections">
                                                    <TemplatesGalleryComponent
                                                      locale={locale}
                                                      productId={mpv.coreProductId}
                                                      mpvId={mpv.mpvId}
                                                    />
                                                  </Box>
                                                </ErrorBoundary>
                                                <Box marginY="between-sections">
                                                  <ProductPageTabs
                                                    pageData={data}
                                                    simpleLinks={{
                                                      offlineEditorMacDownload:
                                                        data.offlineEditorMacDownload,
                                                      offlineEditorWindowsDownload:
                                                        data.offlineEditorWindowsDownload,
                                                      onlinePhotobookEditor:
                                                        data.onlinePhotobookEditor,
                                                    }}
                                                    productBuilderOptions={
                                                      pageContext.productBuilderOptions
                                                        ?.selections ?? []
                                                    }
                                                  />
                                                </Box>
                                                <Box marginY="between-sections">
                                                  <PluginPlaceholder
                                                    placeholderType={Placeholder.AfterTab}
                                                  />
                                                </Box>
                                                <Box marginY="between-sections">
                                                  <MailingServiceTermsOfUse />
                                                </Box>
                                                <ErrorBoundary>
                                                  <ErrorBoundary>
                                                    <Box marginY="between-sections">
                                                      <RelatedProducts />
                                                    </Box>
                                                  </ErrorBoundary>
                                                  <ErrorBoundary>
                                                    <Box marginY="between-sections">
                                                      <FrequentlyBoughtTogether />
                                                    </Box>
                                                  </ErrorBoundary>
                                                  <ErrorBoundary>
                                                    <Box marginY="between-sections">
                                                      <ProductBrowsingHistory />
                                                    </Box>
                                                  </ErrorBoundary>
                                                  <Box marginY="between-sections">
                                                    <Reviews
                                                      pageData={data}
                                                      reviews={pageContext.reviews}
                                                      locale={locale}
                                                    />
                                                  </Box>
                                                  <Box marginTop="between-sections">
                                                    <DesignServices />
                                                  </Box>
                                                </ErrorBoundary>
                                              </DesignServicesContextProvider>
                                            </SiteMain>
                                            <Footer
                                              type={BOOKEND_TYPES.FULL}
                                              locale={locale}
                                              tenant={PageConfig.tenant}
                                              deepLink={{
                                                id: mpv.mpvId,
                                                pageType: "productpage",
                                              }}
                                              trackingConfiguration={
                                                pageContext.trackingConfiguration
                                              }
                                            />
                                          </SiteContent>
                                        </CouponCalloutProvider>
                                      </ScreenClassProvider>
                                      <Analytics />
                                    </PluginStateProvider>
                                  </PluginRegistryProvider>
                                </ProductPageTabContextProvider>
                              </ProductBuilderContextProvider>
                            </ReferentialImageContextProvider>
                          </AnalyticsContextProvider>
                        </BffDataContextProvider>
                      </FeedTemplateProvider>
                    </GalleryTemplateProvider>
                  </CampaignProvider>
                </ExperimentsProvider>
              </ProductContextProvider>
            </PricingContextProvider>
          </IdentityProvider>
        </PageStaticDataContextProvider>
      </SnackbarContextProvider>
    </>
  );
};

const Page = ({ data, pageContext }: { data: any; pageContext: PageContext }) => {
  const { item, seo } = pageContext;

  const seoMetadata: SeoMetadata = {
    title: seo?.metaTitle ?? data?.mpv?.name,
    description: seo?.metaDescription ?? "",
    robots: seo?.metaRobot ?? "",
    canonicalUrl: seo?.canonicalUrl ?? "",
  };

  return (
    <React.Fragment>
      <MetadataWrapper
        seoMetadata={seoMetadata}
        trackingConfiguration={pageContext.trackingConfiguration}
      />
      <Helmet>
        <html lang={pageContext.locale} />
        <script type="text/javascript">{pageContext.abReader.scriptBody}</script>
      </Helmet>
      <BookendsMetaTag
        inlineCriticalCss={true}
        renderWith={Helmet}
        locale={pageContext.locale}
        tenant={PageConfig.tenant}
        showStickyHeader={true}
        headerType={BOOKEND_TYPES.FULL}
        footerType={BOOKEND_TYPES.FULL}
      />
      <SwanConfiguration pluginStyleSheetKeys={pageContext.allPluginsStyles}>
        <DebugContextProvider>
          <Index data={data} pageContext={pageContext} />
        </DebugContextProvider>
      </SwanConfiguration>
    </React.Fragment>
  );
};

export default Page;
